// Does the approval in hand authorise the act about to be performed?
//
// The server asks this question and is the authority on the answer:
// `verifyControlPlaneProposalBinding` re-derives all five committed fields —
// domain, operation, productId, lane, target — and refuses anything else. This
// module is NOT a second copy of that gate and does not make an execution
// safe; it makes a REFUSAL LEGIBLE, and it stops the console from knowingly
// sending a pair it can already see does not match.
//
// Two reasons that is worth having.
//
// 1. A refused execute is not free. It costs a round trip, a rejected-attempt
//    audit row, and an operator's confidence — and the server's refusal
//    arrives as `403 reason=wrong_target`, which is precise but arrives after
//    the operator has already committed to the act.
//
// 2. §15 of the operator brief is a UI requirement, not only a server one: the
//    console must never *offer* proposal-for-A → execute-B. Reading the
//    proposal's own payload and comparing it to the act on screen is how a
//    screen can honour that instead of asserting it.
//
// WHAT THIS DOES NOT DO. It does not decide the lane (the server does, from
// its own issuer), does not decide the threshold, does not decide the risk
// class, and never treats a local `ok` as permission. Every `ok` here means
// only "nothing visible disagrees" — the server still runs the real check, and
// a disagreement it finds and this does not is the server's to refuse.

import type { Lane } from "./contract";
import type { ControlPlaneOperation } from "./api";
import type { ProposalDetailResponse } from "../api/approvals";

/** The payload domain every Control Plane proposal commits to. Mirrors
 *  `CONTROL_PLANE_PAYLOAD_DOMAIN` in the Worker. */
export const CONTROL_PLANE_PAYLOAD_DOMAIN = "product.control_plane";

/** What the console is about to do, in the same five terms the proposal
 *  payload uses. */
export interface ControlPlaneAct {
  operation: ControlPlaneOperation;
  productId: string;
  /** The lane the SERVER reported for this tier. Never chosen here. */
  lane: Lane | null;
  /** publish → the draft's document hash; rollback → the target revision id;
   *  bootstrap → `genesis-content-sha256:<digest>` over the registry-derived
   *  content (a proposal minted before that binding carries null and is
   *  refused at execution). */
  target: string | null;
}

/** The five fields as stored on the proposal. */
export interface ProposalBinding {
  domain: string;
  operation: string;
  productId: string;
  lane: string;
  target: string | null;
}

export type BindingMismatch =
  | "unreadable"
  | "wrong_domain"
  | "wrong_operation"
  | "wrong_product"
  | "wrong_lane"
  | "wrong_target"
  | "lane_unknown";

export type BindingCheck =
  | { ok: true; binding: ProposalBinding }
  | { ok: false; mismatch: BindingMismatch; detail: string; binding: ProposalBinding | null };

/** Read the redacted payload the approvals BFF returns alongside a proposal.
 *  Returns null for any shape that is not a Control Plane payload — including
 *  a payload from another family, which is exactly the case a bare
 *  "does the id look right" check would wave through. */
export function readBinding(payload: unknown): ProposalBinding | null {
  if (payload === null || typeof payload !== "object") return null;
  const p = payload as Record<string, unknown>;
  if (typeof p.domain !== "string") return null;
  if (typeof p.operation !== "string") return null;
  if (typeof p.productId !== "string") return null;
  if (typeof p.lane !== "string") return null;
  // `null` is a real stored value (a pre-binding bootstrap proposal), so
  // absence and null are the same thing here and neither is coerced to a
  // string.
  const target = p.target === undefined || p.target === null ? null : p.target;
  if (target !== null && typeof target !== "string") return null;
  return {
    domain: p.domain,
    operation: p.operation,
    productId: p.productId,
    lane: p.lane,
    target,
  };
}

const OPERATION_TEXT: Readonly<Record<string, string>> = {
  bootstrap: "bootstrap",
  publish: "publish",
  rollback: "rollback",
};

function opText(v: string): string {
  return OPERATION_TEXT[v] ?? v;
}

/**
 * Compare a proposal against the act on screen.
 *
 * Order mirrors the server's, so a console-side refusal names the same
 * dimension the server would have named. The one branch that has no server
 * counterpart is `lane_unknown`: the server always knows its own lane, and the
 * console sometimes does not (the registry read failed). Refusing there is
 * deliberate — a comparison against an unknown lane can only be made by
 * assuming one, and assuming the safe-sounding lane is how a DEV approval ends
 * up pointed at Product.
 */
export function checkBinding(
  act: ControlPlaneAct,
  detail: ProposalDetailResponse,
): BindingCheck {
  const binding = readBinding(detail.payload);
  if (binding === null) {
    return {
      ok: false,
      mismatch: "unreadable",
      detail:
        "This proposal's payload is not a Control Plane payload, so there is " +
        "nothing to compare it against.",
      binding: null,
    };
  }
  if (binding.domain !== CONTROL_PLANE_PAYLOAD_DOMAIN) {
    return {
      ok: false,
      mismatch: "wrong_domain",
      detail: `This proposal belongs to "${binding.domain}", not the Control Plane.`,
      binding,
    };
  }
  if (binding.operation !== act.operation) {
    return {
      ok: false,
      mismatch: "wrong_operation",
      detail: `This proposal approves a ${opText(binding.operation)}, not a ${opText(act.operation)}.`,
      binding,
    };
  }
  if (binding.productId !== act.productId) {
    return {
      ok: false,
      mismatch: "wrong_product",
      detail: `This proposal is for ${binding.productId}, not ${act.productId}.`,
      binding,
    };
  }
  if (act.lane === null) {
    return {
      ok: false,
      mismatch: "lane_unknown",
      detail:
        `This proposal was approved on the ${binding.lane} lane, and this ` +
        `console has not been told which lane it is on. The comparison cannot ` +
        `be made, and assuming a lane is how a dev approval reaches Product.`,
      binding,
    };
  }
  if (binding.lane !== act.lane) {
    return {
      ok: false,
      mismatch: "wrong_lane",
      detail:
        `This proposal was approved on the ${binding.lane} lane; this console ` +
        `is on ${act.lane}. An approval on one lane is never spendable on another.`,
      binding,
    };
  }
  if (binding.target !== act.target) {
    return {
      ok: false,
      mismatch: "wrong_target",
      detail:
        act.operation === "publish"
          ? `This proposal approves the document ${short(binding.target)}; the draft ` +
            `on screen is ${short(act.target)}. The draft has changed since the ` +
            `proposal was created, so the approval no longer covers what would be published.`
          : `This proposal approves ${binding.target ?? "no target"}; this action ` +
            `targets ${act.target ?? "no target"}.`,
      binding,
    };
  }
  return { ok: true, binding };
}

function short(v: string | null): string {
  if (v === null) return "nothing";
  return v.length > 20 ? `${v.slice(0, 8)}…${v.slice(-8)}` : v;
}

/**
 * May the console send this execute?
 *
 * BOTH conditions, and neither implies the other: the server must currently
 * report the proposal executable (status `approved`, not vetoed, not expired),
 * AND the proposal must be bound to this exact act. An approved proposal for
 * another revision is still approved.
 */
export function executeBlockedReason(
  act: ControlPlaneAct,
  detail: ProposalDetailResponse | null,
  serverSaysExecutable: boolean,
): string | null {
  if (detail === null) return "No proposal has been loaded for this action.";
  if (!serverSaysExecutable) {
    return "The server does not report this proposal as approved and spendable.";
  }
  const bound = checkBinding(act, detail);
  return bound.ok ? null : bound.detail;
}
